import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Crown, Check, Loader2, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSubscription } from "@/hooks/useSubscription";
import { startSubscriptionCheckout, startOneTimeCheckout, postApi } from "@/lib/api";
import type { BuyerTier } from "@/lib/buyer-billing";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

type PlanId = "essential" | "essentialPlus" | "acquisitionPro" | "byod";

const PLANS: Record<PlanId, { price: number; priceId: string; mode: "subscription" | "one_time" }> = {
  essential:      { price: 999,  priceId: 'price_1TcybjCkUJGKtqQA6SoIwGB9', mode: "subscription" },
  essentialPlus:  { price: 2199, priceId: 'price_1TcycuCkUJGKtqQAX7BA3dSX', mode: "subscription" },
  acquisitionPro: { price: 3999, priceId: 'price_1TcyhOCkUJGKtqQAuWnc1P59', mode: "subscription" },
  byod:           { price: 499,  priceId: 'price_1Td0YPCkUJGKtqQAf0h1sMue', mode: "one_time" },
};

const PLAN_TO_BUYER_TIER: Partial<Record<PlanId, BuyerTier>> = {
  essential:      "essential",
  essentialPlus:  "essential_plus",
  acquisitionPro: "acquisition_pro",
};

const COPY = {
  en: {
    title: "Upgrade your Buyer plan",
    subtitle: "Unlock more deals and tools by upgrading your subscription.",
    current: "Current plan",
    select: "Choose this plan",
    switch: "Switch to this plan",
    redirecting: "Redirecting to secure checkout…",
    perYear: "/year",
    oneTime: "one-time",
    notAuth: "Please sign in to upgrade.",
    error: "Could not start checkout. Please try again.",
    plans: {
      essential:      { name: "Essential",       desc: "Browse all listings, NDAs, and core deal flow." },
      essentialPlus:  { name: "Essential Plus",  desc: "Priority support, market reports and Elite Buyer badge." },
      acquisitionPro: { name: "Acquisition Pro", desc: "All Essential Plus benefits, unlimited reports and reduced fees." },
      byod:           { name: "BYOD — Bring Your Own Deal", desc: "Submit your own deal with professional evaluation." },
    },
    features: {
      essential:      ["Access to all listings", "Sign NDAs", "Direct messaging", "Document storage"],
      essentialPlus:  ["Everything in Essential", "Priority support", "Market analysis reports", "Elite Buyer badge", "Early access to new listings"],
      acquisitionPro: ["All Essential Plus benefits", "Unlimited Deal Quality Reports", "Unlimited Financing Feasibility Reports", "3 Market Studies/year", "Reduced fee: 0.75%"],
      byod:           ["Submit your own deal", "Financial evaluation", "Access to partner lenders", "6-month Data Room"],
    },
  },
  fr: {
    title: "Améliorez votre forfait Acheteur",
    subtitle: "Débloquez plus de deals et d'outils en améliorant votre abonnement.",
    current: "Forfait actuel",
    select: "Choisir ce forfait",
    switch: "Basculer vers ce forfait",
    redirecting: "Redirection vers le paiement sécurisé…",
    perYear: "/an",
    oneTime: "paiement unique",
    notAuth: "Veuillez vous connecter pour améliorer votre forfait.",
    error: "Impossible de démarrer le paiement. Réessayez.",
    plans: {
      essential:      { name: "Essentiel",       desc: "Toutes les annonces, NDA et flux de transaction." },
      essentialPlus:  { name: "Essentiel Plus",  desc: "Support prioritaire, rapports marché, badge Elite." },
      acquisitionPro: { name: "Acquisition Pro", desc: "Tous les avantages Essential Plus, rapports illimités et frais réduits." },
      byod:           { name: "BYOD — Apportez votre propre deal", desc: "Soumettez votre deal avec évaluation pro." },
    },
    features: {
      essential:      ["Accès à toutes les annonces", "Signature de NDA", "Messagerie directe", "Stockage de documents"],
      essentialPlus:  ["Tout d'Essentiel", "Support prioritaire", "Rapports d'analyse de marché", "Badge Elite Buyer", "Accès anticipé aux nouvelles annonces"],
      acquisitionPro: ["Tous les avantages Essential Plus", "Rapports Deal Quality illimités", "Rapports Financing Feasibility illimités", "3 Market Studies/an", "Frais réduits : 0,75%"],
      byod:           ["Soumettez votre propre deal", "Évaluation financière", "Accès aux prêteurs partenaires", "Data Room 6 mois"],
    },
  },
} as const;

type UpgradePreview = {
  amountDueToday: number;
  creditFromCurrentPlan: number;
  chargeForNewPlan: number;
  nextBillingAmount: number;
  nextBillingDate: string | null;
  currency: string;
  currentCard: { brand: string; last4: string } | null;
};

export default function BuyerUpgrade() {
  const { user, loading } = useAuth();
  const { language } = useLanguage();
  const { tier } = useSubscription();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { toast } = useToast();
  const [busy, setBusy] = useState<PlanId | null>(null);

  const [upgradeDialog, setUpgradeDialog] = useState<{
    open: boolean;
    planId: PlanId | null;
    loading: boolean;
    preview: UpgradePreview | null;
    error: string | null;
  }>({ open: false, planId: null, loading: false, preview: null, error: null });

  const t = COPY[(language as "en" | "fr") || "en"];
  const presetPlan = (params.get("plan") || "").trim() as PlanId | "";

  const hasActiveBuyerSub = useMemo(
    () => tier === "essential" || tier === "essential_plus" || tier === "acquisition_pro",
    [tier]
  );

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const next = encodeURIComponent(`/buyer/upgrade${presetPlan ? `?plan=${presetPlan}` : ""}`);
      navigate(`/login?next=${next}`, { replace: true });
    }
  }, [loading, user, navigate, presetPlan]);

  const handleCheckout = async (planId: PlanId) => {
    if (!user) {
      toast({ title: t.notAuth, variant: "destructive" });
      return;
    }
    setBusy(planId);
    try {
      const fn = PLANS[planId].mode === "subscription" ? startSubscriptionCheckout : startOneTimeCheckout;
      const { url } = await fn({
        planId,
        userId: user.id,
        lang: (language as "en" | "fr") || "en",
        termsVersion: "1.0",
      });
      if (!url) throw new Error("Missing checkout URL");
      window.location.href = url;
    } catch (err) {
      toast({ title: t.error, description: String((err as Error)?.message || ""), variant: "destructive" });
      setBusy(null);
    }
  };

  const handleSelect = async (planId: PlanId) => {
    if (hasActiveBuyerSub && PLAN_TO_BUYER_TIER[planId]) {
      const isCurrent =
        (planId === "essential" && tier === "essential") ||
        (planId === "essentialPlus" && tier === "essential-plus") ||
        (planId === "acquisitionPro" && tier === "acquisition_pro");
      if (isCurrent) return;

      setUpgradeDialog({ open: true, planId, loading: true, preview: null, error: null });
      try {
        const { getAuth } = await import("firebase/auth");
        const currentUser = getAuth().currentUser;
        if (currentUser) await currentUser.getIdToken(true);
        const newTier = PLAN_TO_BUYER_TIER[planId];
        const data = await postApi("/payments/preview-subscription-change", { newTier });
        setUpgradeDialog((prev) => ({ ...prev, loading: false, preview: data }));
      } catch (err: any) {
        setUpgradeDialog((prev) => ({
          ...prev,
          loading: false,
          error: err?.message || "Preview failed",
        }));
      }
    } else {
      handleCheckout(planId);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!upgradeDialog.planId) return;
    const newTier = PLAN_TO_BUYER_TIER[upgradeDialog.planId];
    if (!newTier) return;
    setBusy(upgradeDialog.planId);
    try {
      const { getAuth } = await import("firebase/auth");
      const currentUser = getAuth().currentUser;
      if (currentUser) await currentUser.getIdToken(true);
      await postApi("/payments/change-subscription", { newTier });
      setUpgradeDialog({ open: false, planId: null, loading: false, preview: null, error: null });
      toast({
        title: language === "fr" ? "Forfait mis à jour !" : "Plan upgraded!",
        description: language === "fr"
          ? "Votre abonnement a été mis à jour avec prorata."
          : "Your subscription has been updated with proration.",
      });
      navigate("/buyer/dashboard");
    } catch (err: any) {
      toast({
        title: language === "fr" ? "Erreur" : "Error",
        description: err?.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  };

  const formatAmount = (cents: number, currency: string) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(Math.abs(cents) / 100);

  useEffect(() => {
    if (loading || !user || !presetPlan) return;
    if (!(presetPlan in PLANS)) return;
    if (busy || hasActiveBuyerSub) return;
    handleCheckout(presetPlan as PlanId);
  }, [loading, user, presetPlan, hasActiveBuyerSub]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <Crown className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-3xl font-bold">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(Object.keys(PLANS) as PlanId[]).map((id) => {
            const plan = PLANS[id];
            const isCurrent =
              (id === "essential" && tier === "essential") ||
              (id === "essentialPlus" && tier === "essential-plus") ||
              (id === "acquisitionPro" && tier === "acquisition_pro") ||
              (id === "byod" && tier === "byod");

            const showSwitchLabel =
              hasActiveBuyerSub && !!PLAN_TO_BUYER_TIER[id] && !isCurrent;

            return (
              <Card key={id} className={isCurrent ? "border-primary" : ""}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{t.plans[id].name}</CardTitle>
                    {isCurrent && <Badge variant="secondary">{t.current}</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">{t.plans[id].desc}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <span className="text-3xl font-bold">${plan.price.toLocaleString()}</span>
                    <span className="text-muted-foreground ml-1 text-sm">
                      {plan.mode === "subscription" ? t.perYear : t.oneTime}
                    </span>
                  </div>
                  <ul className="space-y-2 text-sm">
                    {t.features[id].map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full"
                    disabled={isCurrent || busy !== null}
                    onClick={() => handleSelect(id)}
                  >
                    {busy === id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t.redirecting}
                      </>
                    ) : showSwitchLabel ? t.switch : t.select}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <Dialog
        open={upgradeDialog.open}
        onOpenChange={(open) =>
          !open && setUpgradeDialog((prev) => ({ ...prev, open: false }))
        }
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {language === "fr" ? "Confirmer la mise à niveau" : "Confirm Upgrade"}
            </DialogTitle>
            <DialogDescription>
              {upgradeDialog.planId ? t.plans[upgradeDialog.planId].name : ""}
            </DialogDescription>
          </DialogHeader>

          {upgradeDialog.loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              {language === "fr" ? "Calcul du prorata..." : "Calculating proration..."}
            </div>
          )}

          {upgradeDialog.error && (
            <div className="flex items-start gap-2 text-sm text-destructive py-4">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{upgradeDialog.error}</span>
            </div>
          )}

          {upgradeDialog.preview && !upgradeDialog.loading && (
            <div className="space-y-4 py-2">
              <div className="rounded-md border p-4 space-y-3">
                <div className="text-sm font-medium">
                  {language === "fr" ? "Détail du prorata" : "Proration Details"}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {language === "fr" ? "Crédit plan actuel" : "Credit from current plan"}
                    </span>
                    <span className="font-medium text-green-600">
                      −{formatAmount(Math.abs(upgradeDialog.preview.creditFromCurrentPlan), upgradeDialog.preview.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {language === "fr" ? "Charge nouveau plan" : "Charge for new plan"}
                    </span>
                    <span className="font-medium">
                      +{formatAmount(upgradeDialog.preview.chargeForNewPlan, upgradeDialog.preview.currency)}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-base">
                    <span className="font-semibold">
                      {language === "fr" ? "Dû aujourd'hui" : "Due today"}
                    </span>
                    <span className="font-bold">
                      {formatAmount(upgradeDialog.preview.amountDueToday, upgradeDialog.preview.currency)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="text-xs text-muted-foreground space-y-1">
                <div>
                  {language === "fr" ? "Prochain renouvellement :" : "Next renewal:"}{" "}
                  <span className="font-medium text-foreground">
                    {upgradeDialog.preview.nextBillingDate
                      ? new Date(upgradeDialog.preview.nextBillingDate).toLocaleDateString(
                          language === "fr" ? "fr-CA" : "en-US",
                          { year: "numeric", month: "long", day: "numeric" }
                        )
                      : "—"}
                  </span>
                  {" · "}
                  {formatAmount(upgradeDialog.preview.nextBillingAmount, upgradeDialog.preview.currency)}
                  {language === "fr" ? "/an" : "/year"}
                </div>
                {upgradeDialog.preview.currentCard && (
                  <div>
                    {language === "fr" ? "Carte :" : "Card:"}{" "}
                    <span className="font-medium text-foreground capitalize">
                      {upgradeDialog.preview.currentCard.brand} ••••{" "}
                      {upgradeDialog.preview.currentCard.last4}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUpgradeDialog((prev) => ({ ...prev, open: false }))}
              disabled={busy !== null}
            >
              {language === "fr" ? "Annuler" : "Cancel"}
            </Button>
            <Button
              onClick={handleConfirmUpgrade}
              disabled={!upgradeDialog.preview || busy !== null || upgradeDialog.loading}
              className="bg-[#1D314D] hover:bg-[#2E4663] text-white"
            >
              {busy !== null && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {language === "fr" ? "Confirmer la mise à niveau" : "Confirm Upgrade"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}